import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mcpCall, mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import './Settings.css';

/**
 * The system settings page (backlog 507). cabana.system_settings holds the business numbers
 * Mike asked to be flexible — the anchor size, the cadence options, the affiliate discount
 * scope, the co-op earn rate, the dealer default discount and minimum order, the returns policy.
 * Until now they changed only through the MCP or an UPDATE; this is the version an operator uses.
 *
 * Each value is edited as JSON, because the shapes differ (a number, a string, a list of days,
 * an object) and the MCP validates and falls back on a bad shape anyway. The description from
 * the setting itself tells the operator what shape each key expects, so nothing is hard-coded
 * here — a setting added to the table appears without a code change.
 */
interface Setting {
  key: string;
  value: unknown;
  description: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export function Settings() {
  const qc = useQueryClient();
  const { can } = useMcpTools();
  const { notify } = useToast();
  const canWrite = can('setSetting');

  const q = useQuery({
    queryKey: ['mcp', 'listSettings'],
    queryFn: () => mcpCall<{ settings: Setting[] }>('listSettings'),
  });

  if (q.isPending) return <div className="page"><h1 className="page__title">Settings</h1><p className="page__muted">Loading…</p></div>;
  if (q.isError) return <div className="page"><h1 className="page__title">Settings</h1><p className="page__muted">Could not load settings: {(q.error as Error).message}</p></div>;

  const settings = q.data?.settings ?? [];

  return (
    <div className="page">
      <h1 className="page__title">Settings</h1>
      <p className="page__muted">
        The business numbers and rules, held as data so they change without a deploy. Each value is JSON of
        the shape its description names; a bad shape is ignored and the default stands.
        {!canWrite && ' Your role can view these but not change them.'}
      </p>
      <div className="settings__list">
        {settings.map((s) => (
          <SettingRow
            key={s.key}
            setting={s}
            canWrite={canWrite}
            onSaved={() => {
              notify({ variant: 'success', message: `Saved ${s.key}.` });
              void qc.invalidateQueries({ queryKey: ['mcp', 'listSettings'] });
            }}
            onError={(m) => notify({ variant: 'error', message: m })}
          />
        ))}
      </div>
    </div>
  );
}

function SettingRow({ setting, canWrite, onSaved, onError }: {
  setting: Setting;
  canWrite: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState(() => JSON.stringify(setting.value));
  const dirty = draft !== JSON.stringify(setting.value);

  const save = useMutation({
    mutationFn: async () => {
      let value: unknown;
      try {
        value = JSON.parse(draft);
      } catch {
        throw new Error('That is not valid JSON. A string needs quotes, e.g. "20x25x1".');
      }
      return mcpWrite('setSetting', { key: setting.key, value });
    },
    onSuccess: onSaved,
    onError: (e) => onError((e as Error).message),
  });

  return (
    <div className="settings__row">
      <div className="settings__meta">
        <code className="settings__key">{setting.key}</code>
        {setting.description && <p className="settings__desc">{setting.description}</p>}
        {setting.updatedAt && (
          <p className="settings__stamp">
            Last changed {new Date(setting.updatedAt).toLocaleString()}
            {setting.updatedBy ? ` by ${setting.updatedBy}` : ''}
          </p>
        )}
      </div>
      <div className="settings__edit">
        <input
          className="settings__input"
          value={draft}
          spellCheck={false}
          disabled={!canWrite || save.isPending}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={`Value for ${setting.key}`}
        />
        {canWrite && (
          <button
            type="button"
            className="settings__save"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}
